import { useState } from 'react';
import { Church } from '../../../mocks/churches';

interface ChurchModalProps {
  church: Church;
  onClose: () => void;
}

export default function ChurchModal({ church, onClose }: ChurchModalProps) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleShare = (platform: string) => {
    const churchUrl = `${window.location.origin}/church/${church.id}`;
    const shareText = `Check out ${church.name} in ${church.location}`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + churchUrl)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(churchUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(churchUrl).then(() => {
          setCopySuccess(true);
          setTimeout(() => {
            setCopySuccess(false);
            setShowShareMenu(false);
          }, 2000);
        });
        break;
    }

    if (platform !== 'copy') {
      setShowShareMenu(false);
    }
  };

  // Extended church data
  const churchData = {
    ...church,
    fullAddress: `${church.location}, Nigeria`,
    pastor: 'Pastor Emmanuel Adeyemi',
    email: `info@${church.name.toLowerCase().replace(/\s+/g, '')}.sda.ng`,
    phone: '+234 803 456 7890',
    founded: '2005',
    services: [
      { day: 'Saturday', time: '9:00 AM - 12:00 PM', type: 'Sabbath School &amp; Divine Service' },
      { day: 'Wednesday', time: '6:00 PM - 7:30 PM', type: 'Prayer Meeting' },
      { day: 'Friday', time: '6:00 PM - 7:00 PM', type: 'Vespers Service' },
    ],
    ministries: [
      'Youth Ministry',
      'Women\'s Ministry',
      'Men\'s Ministry',
      'Children\'s Ministry',
      'Music Ministry',
      'Health Ministry',
      'Community Services',
    ],
    facilities: [
      'Main Sanctuary (500 capacity)',
      'Sabbath School Rooms',
      'Fellowship Hall',
      'Children\'s Area',
      'Parking Space',
      'Audio/Visual Equipment',
    ],
    description: `${church.name} is a vibrant community of believers committed to spreading the gospel and serving our community. We welcome all who seek to worship God and grow in their faith. Our church family is diverse, warm, and dedicated to living out the teachings of Jesus Christ.`,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-5xl w-full my-8 shadow-2xl max-h-[95vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="sticky top-4 float-right mr-4 w-10 h-10 flex items-center justify-center bg-white hover:bg-gray-100 rounded-full transition-colors cursor-pointer z-10 shadow-lg"
        >
          <i className="ri-close-line text-gray-700 text-xl"></i>
        </button>

        {/* Hero Section */}
        <div className="relative h-72 overflow-hidden rounded-t-2xl">
          <img
            src={church.image}
            alt={church.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {church.livestream && (
                <span className="px-3 py-1 bg-rose-500 text-white text-sm font-semibold rounded-full flex items-center space-x-1">
                  <i className="ri-live-line"></i>
                  <span className="whitespace-nowrap">Live Streaming</span>
                </span>
              )}
              <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                Active
              </span>
              {church.distance !== undefined && (
                <span className="px-3 py-1 bg-teal-500 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                  {church.distance.toFixed(1)} km away
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              {church.name}
            </h1>
            <div className="flex items-center text-white/95 text-base">
              <i className="ri-map-pin-line mr-2"></i>
              <span>{church.location}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* About Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-information-line text-emerald-600 mr-2"></i>
                  About Us
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                  {churchData.description}
                </p>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Founded</p>
                    <p className="text-base font-semibold text-gray-900">{churchData.founded}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Members</p>
                    <p className="text-base font-semibold text-gray-900">{church.members}+</p>
                  </div>
                </div>
              </div>

              {/* Service Times */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-calendar-line text-emerald-600 mr-2"></i>
                  Service Times
                </h2>
                <div className="space-y-3">
                  {churchData.services.map((service, index) => (
                    <div key={index} className="flex items-start justify-between p-3 bg-white rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm mb-1">{service.type}</h3>
                        <p className="text-xs text-gray-600">{service.day}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-600 text-sm">{service.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ministries */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-team-line text-emerald-600 mr-2"></i>
                  Ministries
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {churchData.ministries.map((ministry, index) => (
                    <div key={index} className="flex items-center p-2 bg-white rounded-lg">
                      <i className="ri-checkbox-circle-fill text-emerald-600 mr-2 text-sm"></i>
                      <span className="text-gray-900 text-sm">{ministry}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Facilities */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-building-2-line text-emerald-600 mr-2"></i>
                  Facilities
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {churchData.facilities.map((facility, index) => (
                    <div key={index} className="flex items-center p-2 bg-white rounded-lg">
                      <i className="ri-check-line text-emerald-600 mr-2 text-sm"></i>
                      <span className="text-gray-900 text-sm">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Contact & Details */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-emerald-600"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs text-gray-600 mb-1">Pastor</p>
                      <p className="font-semibold text-gray-900 text-sm">{churchData.pastor}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-emerald-600"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs text-gray-600 mb-1">Email</p>
                      <a href={`mailto:${churchData.email}`} className="font-semibold text-emerald-600 hover:text-emerald-700 cursor-pointer text-sm break-all">
                        {churchData.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-emerald-600"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs text-gray-600 mb-1">Phone</p>
                      <a href={`tel:${churchData.phone}`} className="font-semibold text-emerald-600 hover:text-emerald-700 cursor-pointer text-sm">
                        {churchData.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-emerald-600"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-xs text-gray-600 mb-1">Address</p>
                      <p className="font-semibold text-gray-900 text-sm">{churchData.fullAddress}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(churchData.fullAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors text-center flex items-center justify-center cursor-pointer text-sm"
                  >
                    <i className="ri-map-pin-line mr-2"></i>
                    Get Directions
                  </a>
                  
                  <div className="relative">
                    <button 
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center cursor-pointer text-sm"
                    >
                      <i className="ri-share-line mr-2"></i>
                      Share Church
                    </button>

                    {showShareMenu && (
                      <>
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setShowShareMenu(false)}
                        ></div>
                        <div className="absolute right-0 bottom-full mb-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                          <button
                            onClick={() => handleShare('facebook')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                          >
                            <i className="ri-facebook-fill text-blue-600"></i>
                            Facebook
                          </button>
                          <button
                            onClick={() => handleShare('twitter')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                          >
                            <i className="ri-twitter-x-fill text-gray-900"></i>
                            Twitter
                          </button>
                          <button
                            onClick={() => handleShare('whatsapp')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                          >
                            <i className="ri-whatsapp-fill text-green-600"></i>
                            WhatsApp
                          </button>
                          <button
                            onClick={() => handleShare('email')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                          >
                            <i className="ri-mail-line text-gray-600"></i>
                            Email
                          </button>
                          <div className="border-t border-gray-100 my-1"></div>
                          <button
                            onClick={() => handleShare('copy')}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                          >
                            {copySuccess ? (
                              <>
                                <i className="ri-check-line text-green-600"></i>
                                <span className="text-green-600">Link Copied!</span>
                              </>
                            ) : (
                              <>
                                <i className="ri-link text-gray-600"></i>
                                Copy Link
                              </>
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Organization Details */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Organization</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Conference</p>
                    <p className="font-semibold text-gray-900 text-sm">{church.conference}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Union</p>
                    <p className="font-semibold text-gray-900 text-sm">{church.union}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Region</p>
                    <p className="font-semibold text-gray-900 text-sm">{church.region}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">State</p>
                    <p className="font-semibold text-gray-900 text-sm">{church.state}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
